import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useRefresh } from "../contexts/RefreshContext";
import SortableGrid from "../components/SortableGrid";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { Warning, Package, CurrencyDollar, Info, DownloadSimple } from "@phosphor-icons/react";

function Tile({ label, value, sub, color, icon: Icon }) {
  return (
    <div
      data-testid={`sc-tile-${label.toLowerCase().replace(/\s|%/g, "-")}`}
      style={{
        background: "white", borderTop: "1px solid var(--jde-border)",
        borderRight: "1px solid var(--jde-border)", borderBottom: "1px solid var(--jde-border)",
        borderLeft: `4px solid ${color}`, padding: "12px 14px", borderRadius: 4,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
      }}
    >
      <div>
        <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600, color: "#64748B" }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 3, fontFamily: "var(--jde-font-mono)", color: "#0F172A" }}>{value}</div>
        {sub && <div style={{ fontSize: 10.5, marginTop: 2, color: "#64748B" }}>{sub}</div>}
      </div>
      {Icon && <Icon size={26} weight="duotone" color={color} style={{ opacity: 0.7 }} />}
    </div>
  );
}

export default function ShortClosed() {
  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState("");
  const { tick } = useRefresh();

  useEffect(() => {
    setData(null);
    api.get("/import/short-closed").then((r) => setData(r.data)).catch(() => setData({ rows: [], count: 0, customers: [] }));
  }, [tick]);

  const rows = useMemo(
    () => (data?.rows || []).filter((r) => !customer || r.customer_name === customer),
    [data, customer]
  );

  const exportCsv = () => {
    const head = ["Customer PO", "Customer", "Item", "Description", "Ordered", "Dispatched", "Short (Ordered−Dispatched)", "Unit Price", "Short Value", "Due Date", "Status", "Source File"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((r) => [
      r.ms_custpo, r.customer_name, r.item, r.description, r.qty_ordered, r.qty_shipped,
      r.qty_short, r.unit_price, r.short_value, r.due_date ? fmtDate(r.due_date) : "", r.status, r.source_file,
    ].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "short_closed_pos.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Layout breadcrumb={["Home", "Short-Closed POs"]}>
      <div className="jde-page">
        <div className="jde-toolbar" data-testid="sc-toolbar">
          <div className="jde-toolbar-section">
            <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>CUSTOMER</span>
            <select className="jde-input" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="sc-filter-customer">
              <option value="">All customers</option>
              {(data?.customers || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <span className="muted" style={{ fontSize: 11, marginLeft: "auto" }}>
            <Info size={12} weight="bold" style={{ verticalAlign: "-2px", marginRight: 3 }} />
            PO lines marked closed (open qty = 0) where Ordered &gt; Dispatched.
          </span>
        </div>

        {!data ? (
          <div className="muted" style={{ padding: 24 }} data-testid="sc-loading">Loading reconciliation…</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 8 }}>
              <Tile label="Short-Closed Lines" value={fmtNum(rows.length)} sub="closed but under-shipped" color="#B91C1C" icon={Warning} />
              <Tile label="Total Short Qty" value={fmtNum(rows.reduce((s, r) => s + (r.qty_short || 0), 0))} sub="ordered − dispatched" color="#B45309" icon={Package} />
              <Tile label="Total Short Value" value={fmtCur(rows.reduce((s, r) => s + (r.short_value || 0), 0), "USD")} sub="unit price × short qty" color="#2A5C9B" icon={CurrencyDollar} />
            </div>

            <div className="jde-panel" style={{ marginTop: 16 }}>
              <div className="jde-panel-header">
                <div className="jde-panel-title">Short-Closed POs ({fmtNum(rows.length)})</div>
                <button className="jde-btn jde-btn-sm" onClick={exportCsv} disabled={!rows.length} data-testid="sc-export-csv">
                  <DownloadSimple size={13} weight="bold" style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  Export CSV
                </button>
              </div>
              <div className="jde-panel-body" style={{ padding: 0 }}>
                <SortableGrid pageSize={100}
                  columns={[
                    { key: "ms_custpo", header: "Customer PO", render: (r) => <strong className="text-mono">{r.ms_custpo || "—"}</strong> },
                    { key: "customer_name", header: "Customer", render: (r) => <span>{r.customer_name}</span> },
                    { key: "item", header: "Item", render: (r) => <span className="text-mono">{r.item}</span> },
                    { key: "description", header: "Description", render: (r) => <span style={{ fontSize: 11 }}>{r.description || "—"}</span> },
                    { key: "qty_ordered", header: "Ordered", align: "right", accessor: (r) => r.qty_ordered || 0,
                      render: (r) => <span className="text-mono">{fmtNum(r.qty_ordered)}</span> },
                    { key: "qty_shipped", header: "Dispatched", align: "right", accessor: (r) => r.qty_shipped || 0,
                      render: (r) => <span className="text-mono">{fmtNum(r.qty_shipped)}</span> },
                    { key: "qty_short", header: "Short", align: "right", accessor: (r) => r.qty_short || 0,
                      render: (r) => <span className="text-mono" style={{ fontWeight: 700, color: "#B91C1C" }}>{fmtNum(r.qty_short)}</span> },
                    { key: "short_value", header: "Short Value", align: "right", accessor: (r) => r.short_value || 0,
                      render: (r) => <span className="text-mono">{fmtCur(r.short_value, "USD")}</span> },
                    { key: "due_date", header: "Due date", type: "date", accessor: (r) => r.due_date,
                      render: (r) => <span className="text-mono" style={{ fontSize: 11 }}>{fmtDate(r.due_date)}</span> },
                    { key: "status", header: "Status", render: (r) => <span className="jde-badge closed">{r.status}</span> },
                    { key: "source_file", header: "Source File", render: (r) => <span className="muted" style={{ fontSize: 10.5 }}>{r.source_file}</span> },
                  ]}
                  rows={rows}
                  getRowKey={(r) => `${r.ms_custpo}-${r.item}`}
                  defaultSort={{ key: "qty_short", dir: "desc" }}
                  searchPlaceholder="Search PO, customer, item…"
                  testid="sc-table"
                  emptyMessage="No short-closed POs found — every closed PO was fully dispatched."
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
